/**
 * @fileoverview Civil & Criminal Offences — master review table
 * @module jobs/phases/review/civilCriminalOffences/OffenceMasterTable
 *
 * One row per offence head. Status is decision-aware (Reviewed / In review /
 * Findings identified / No findings); Exposure shows the civil recoverable (or
 * referral count for the pure offence heads); To Review shows pending findings;
 * Agent Status carries the run state. Rows are clickable.
 */

import { Badge } from "@/ui/primitives/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/ui/composed/data-display/table";
import { AlertTriangle, GitPullRequestArrow } from "lucide-react";
import { cn } from "@/utils/misc";
import { AgentText } from "../flaggedTransactions/AgentText";
import { AgentStatusBadge } from "../flaggedTransactions/AgentStatusBadge";
import {
  fmtMoney,
  offenceOutcomeDisplay,
  type OffenceHeadUI,
  type OffenceHeadKey,
} from "./offenceShared";
import { displayProvisionWithDuty } from "../duties";

export function OffenceMasterTable(props: {
  heads: OffenceHeadUI[];
  onOpenHead: (headKey: OffenceHeadKey) => void;
}) {
  const { heads, onOpenHead } = props;

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-background shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Offence Head</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="w-[28%]">Reasoning</TableHead>
            <TableHead className="text-right">Est. Exposure</TableHead>
            <TableHead className="text-right">Actioned</TableHead>
            <TableHead className="text-right">To Review</TableHead>
            <TableHead className="text-right">Confidence</TableHead>
            <TableHead>Agent Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {heads.map((head) => {
            const display = offenceOutcomeDisplay(head);
            return (
              <TableRow
                key={head.headKey}
                className="cursor-pointer"
                onClick={() => onOpenHead(head.headKey)}
              >
                <TableCell>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{head.name}</span>
                    {head.proposedSummary && (
                      <span title="The agent has a proposed update awaiting review">
                        <GitPullRequestArrow className="h-3.5 w-3.5 text-violet-500" />
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    {displayProvisionWithDuty(
                      "offences",
                      head.headKey,
                      head.provision,
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  {head.outcome === "flags_identified" ||
                  head.findingCount > 0 ? (
                    <div className="flex flex-col items-start gap-0.5">
                      <Badge color={display.color} variant="soft" size="sm">
                        {display.label}
                      </Badge>
                      {display.detail && (
                        <span className="text-[10px] text-muted-foreground">
                          {display.detail}
                        </span>
                      )}
                    </div>
                  ) : (
                    <Badge color={display.color} variant="soft" size="sm">
                      {display.label}
                    </Badge>
                  )}
                </TableCell>
                <TableCell>
                  <p className="line-clamp-2 text-xs text-muted-foreground">
                    {head.headline && head.headline !== "Not yet assessed." ? (
                      <AgentText text={head.headline} />
                    ) : (
                      head.uiDescription
                    )}
                  </p>
                </TableCell>
                <TableCell className="text-right">
                  {head.hasQuantum ? (
                    head.decisions.recoverableValue > 0 ? (
                      <span className="font-mono text-sm font-semibold tabular-nums">
                        {fmtMoney(head.decisions.recoverableValue)}
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )
                  ) : head.referralCount > 0 ? (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-rose-700 dark:text-rose-400">
                      <AlertTriangle className="h-3 w-3" />
                      {head.referralCount} referral
                      {head.referralCount === 1 ? "" : "s"}
                    </span>
                  ) : (
                    <span className="text-[11px] text-muted-foreground">
                      referral only
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  {head.decisions.actioned > 0 ? (
                    <div className="flex flex-col items-end">
                      <span className="text-sm font-semibold tabular-nums text-emerald-700 dark:text-emerald-400">
                        {head.decisions.actioned}
                      </span>
                      {head.decisions.actionedValue > 0 && (
                        <span className="text-[11px] text-muted-foreground">
                          {fmtMoney(head.decisions.actionedValue)}
                        </span>
                      )}
                    </div>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <span
                    className={cn(
                      "text-sm font-semibold tabular-nums",
                      head.decisions.pending > 0 &&
                        "text-amber-700 dark:text-amber-400",
                    )}
                  >
                    {head.findingCount > 0 ? head.decisions.pending : "—"}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  <span className="text-xs tabular-nums text-muted-foreground">
                    {head.lastRunAt > 0
                      ? `${Math.round(head.confidence * 100)}%`
                      : "—"}
                  </span>
                </TableCell>
                <TableCell>
                  <AgentStatusBadge
                    status={head.status}
                    basisNote={head.reviewWindow.basisNote}
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
